import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteExhibitor, fetchExhibitor } from '../../../Reducer/keyExhibitorSlice';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Typography
} from '@mui/material';

// Tailwind-style shadow with hover
const shadowHoverStyle = {
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    boxShadow: '0 12px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 12px -6px rgba(0, 0, 0, 0.2)',
    transform: 'scale(1.02)',
  },
};

function UpdateKeyExhibitors() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { status, error } = useSelector((state) => state.keyExhibitor);
  const [localExhibitor, setLocalExhibitor] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    dispatch(fetchExhibitor(id))
      .unwrap()
      .then((data) => {
        if (data?.data?.length === 0) {
          toast.warning('No exhibitors found for this product.');
        }
        setLocalExhibitor(data);
      })
      .catch((err) => toast.error(err.message || 'Failed to fetch exhibitor.'));
  }, [dispatch, id]);

  const handleInputChange = useCallback((event, index) => {
    const { name, value, files } = event.target;

    setLocalExhibitor((prev) => {
      if (!prev || !prev.data) return prev;
      const updatedData = [...prev.data];
      const updatedItem = { ...updatedData[index] };

      if (name === 'img' && files?.[0]) {
        updatedItem[name] = files[0];
        const reader = new FileReader();
        reader.onloadend = () => {
          updatedItem.imgPreview = reader.result;
          updatedData[index] = updatedItem;
          setLocalExhibitor({ ...prev, data: updatedData });
        };
        reader.readAsDataURL(files[0]);
      } else {
        updatedItem[name] = value;
        updatedData[index] = updatedItem;
      }

      return { ...prev, data: updatedData };
    });
  }, []);

  const saveExhibitor = async (key_exhibitor_id) => {
    setIsUpdating(true);
    const exhibitorToUpdate = localExhibitor.data.find((ex) => ex.key_exhibitor_id === key_exhibitor_id);

    if (!exhibitorToUpdate) {
      toast.error('Exhibitor not found!');
      setIsUpdating(false);
      return;
    }

    if (!exhibitorToUpdate.name || !exhibitorToUpdate.description) {
      toast.error('Name and Description are required.');
      setIsUpdating(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('key_exhibitor_id', exhibitorToUpdate.key_exhibitor_id);
      formData.append('name', exhibitorToUpdate.name);
      formData.append('description', exhibitorToUpdate.description);
      if (exhibitorToUpdate.img instanceof File) {
        formData.append('img', exhibitorToUpdate.img);
      }

      await axios.put(`/api/updatekeyexhibitor/${key_exhibitor_id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Exhibitor updated successfully!');
      const data = await dispatch(fetchExhibitor(id)).unwrap();
      setLocalExhibitor(data);
    } catch (err) {
      toast.error(err.message || 'Failed to update exhibitor.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async (key_exhibitor_id) => {
    if (!window.confirm('Are you sure you want to delete this exhibitor?')) return;

    try {
      await dispatch(deleteExhibitor(key_exhibitor_id)).unwrap();
      toast.success('Exhibitor deleted successfully!');
      const data = await dispatch(fetchExhibitor(id)).unwrap();
      setLocalExhibitor(data);
    } catch (error) {
      toast.error(error.message || 'Failed to delete exhibitor.');
    }
  };

  return (
    <div>
      <Typography variant="h4" p={5} align='center' fontWeight={'bold'} gutterBottom>
        Update Key Exhibitor
      </Typography>

      <Grid container spacing={2}>
        {status === 'loading' ? (
          <Typography>Loading...</Typography>
        ) : error ? (
          <Typography color="error">{error.message || error}</Typography>
        ) : localExhibitor?.data?.length > 0 ? (
          localExhibitor.data.map((keyExhibitor, index) => (
            <Grid item xs={12} sm={6} md={4} key={keyExhibitor.key_exhibitor_id}>
              <Card sx={{ ...shadowHoverStyle }}>
                <CardContent>
                  <TextField
                    fullWidth
                    label="Exhibitor Name"
                    name="name"
                    value={keyExhibitor.name || ''}
                    onChange={(event) => handleInputChange(event, index)}
                    margin="normal"
                  />
                  <input
                    type="file"
                    name="img"
                    accept="image/*"
                    onChange={(event) => handleInputChange(event, index)}
                    style={{ display: 'block', marginTop: '10px' }}
                  />
                  {keyExhibitor.imgPreview || keyExhibitor.img ? (
                    <img
                      src={keyExhibitor.imgPreview || keyExhibitor.img}
                      alt={keyExhibitor.name}
                      style={{ width: '100%', marginTop: '10px', borderRadius: 8 }}
                    />
                  ) : null}
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    multiline
                    rows={3}
                    value={keyExhibitor.description || ''}
                    onChange={(event) => handleInputChange(event, index)}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="WooCommerce Product ID"
                    name="woocommerce_product_id"
                    value={keyExhibitor.woocommerce_product_id || ''}
                    InputProps={{ readOnly: true }}
                    margin="normal"
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => saveExhibitor(keyExhibitor.key_exhibitor_id)}
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'Updating...' : 'Update'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    onClick={() => handleDelete(keyExhibitor.key_exhibitor_id)}
                    style={{ marginTop: '10px' }}
                  >
                    Delete
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Typography>No exhibitors found for this product.</Typography>
        )}
      </Grid>

      <Button
        variant="outlined"
        color="secondary"
        onClick={() => navigate('/dashboard/exhibitor-productlist')}
        style={{ marginTop: '25px', marginBottom: '25px' }} // <-- Added margin top and bottom here
      >
        Back to Product List
      </Button>
    </div>
  );
}

export default UpdateKeyExhibitors;
